/**
 * The booking fields a spreadsheet column can map to, in TEMPLATE order — which is also the order
 * Match Columns lists them in, so the two read alike.
 *
 * The passenger leads, then where, then when, then the vehicle, with Notes to Driver last. That is
 * deliberately not the order the wizard collects these in: the wizard asks for Address first because
 * that is the sequence someone fills a form in, whereas a spreadsheet is SCANNED — and you recognise a
 * row by who is travelling, not by an address. Notes is last because it is the only long free text,
 * and in the middle it split the passenger's own details in two. Labels match the shipped template's headers so a file built from it auto-maps
 * end-to-end with nothing for the user to do.
 *
 * `required` mirrors the booking sheet's own gates — see §4e of the plan. Notably Passenger Email is
 * required: a bulk row books for a named passenger (a guest booking), and the sheet requires an
 * email for those, so allowing it here would create bookings the manual flow would reject.
 */
export type BookingFieldKey =
  | "passengerTitle"
  | "passengerFirstName"
  | "passengerLastName"
  | "passengerEmail"
  | "passengerPhone"
  | "pickup"
  | "destination"
  | "pickupDate"
  | "pickupTime"
  | "flightNumber"
  | "arrivalDelay"
  | "transferType"
  | "vehicleType"
  | "extras"
  | "passengerCount"
  | "bagCount"
  | "notesToDriver";

export type BookingField = {
  key: BookingFieldKey;
  /** Shown in the left column of Match Columns, and matched against the file's headers. */
  label: string;
  required: boolean;
  /** Extra header spellings to auto-match, beyond the label itself. */
  aliases?: string[];
};

export const BOOKING_FIELDS: BookingField[] = [
  // Title and Phone because the booking sheet's guest form requires both — it won't submit without
  // them. Bulk shipped without either, so it created bookings the manual flow would have refused, and
  // no driver could ring the passenger.
  { key: "passengerTitle", label: "Title", required: true, aliases: ["Passenger Title", "Salutation"] },
  /*
   * Two columns, not one, because the booking form has two fields and the guess between them can't be
   * made reliably. A single "Passenger" column had to be split on its last space, which reads
   * "Mary Anne Smith" correctly and "Anna van der Berg" as "Anna van der" + "Berg" — silently, with no
   * error, producing a booking under a mangled name.
   */
  { key: "passengerFirstName", label: "First Name", required: true, aliases: ["Passenger First Name", "Given Name", "Firstname"] },
  { key: "passengerLastName", label: "Last Name", required: true, aliases: ["Passenger Last Name", "Surname", "Family Name", "Lastname"] },
  { key: "passengerEmail", label: "Passenger Email", required: true, aliases: ["Email", "Passenger E-mail"] },
  {
    key: "passengerPhone",
    label: "Passenger Phone",
    required: true,
    aliases: ["Phone", "Passenger Contact", "Contact Number", "Mobile", "Phone Number"],
  },
  { key: "pickup", label: "Pick-up Location", required: true, aliases: ["Pickup", "Pickup Location", "From", "Origin"] },
  { key: "destination", label: "Destination", required: true, aliases: ["Drop-off", "Dropoff", "Drop off Location", "To"] },
  { key: "pickupDate", label: "Pickup Date", required: true, aliases: ["Date", "Pick-up Date"] },
  { key: "pickupTime", label: "Pickup Time", required: true, aliases: ["Time", "Pick-up Time"] },
  /*
   * Optional: most bulk trips aren't airport runs. When it IS filled, the booking gets flight tracking
   * for free — BookingFlightAlertListener already fires for every booking, bulk included.
   */
  { key: "flightNumber", label: "Flight Number", required: false, aliases: ["Flight", "Flight No", "Flight No."] },
  /*
   * Only meaningful alongside a flight. Mirrors the booking sheet's "Delay driver arrival by" field,
   * which appears only once a flight has resolved and replaced manual time entry.
   */
  {
    key: "arrivalDelay",
    label: "Delay Driver Arrival",
    required: false,
    aliases: ["Delay Driver Arrival By", "Driver Delay", "Arrival Delay"],
  },
  { key: "transferType", label: "Transfer Type", required: true, aliases: ["Transfer"] },
  { key: "vehicleType", label: "Vehicle Type", required: true, aliases: ["Vehicle", "Vehicle Transfer", "Car Type"] },
  { key: "extras", label: "Extras", required: false, aliases: ["Add-ons", "Addons"] },
  {
    key: "passengerCount",
    label: "# of Passenger",
    required: false,
    aliases: ["# of Passengers", "Passengers", "Pax", "No of Passengers"],
  },
  { key: "bagCount", label: "# of Bags", required: false, aliases: ["Bags", "Luggage", "No of Bags"] },
  { key: "notesToDriver", label: "Notes to Driver", required: false, aliases: ["Note to Driver", "Notes", "Driver Notes"] },
];

/** Maps each booking field to the index of the file column feeding it (or null when unmapped). */
export type ColumnMapping = Record<BookingFieldKey, number | null>;

export const EMPTY_MAPPING: ColumnMapping = BOOKING_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: null }), {} as ColumnMapping);

/** Lowercase, collapse whitespace, drop punctuation — so "Pick-up Location" matches "pickup location". */
export const normaliseHeader = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Levenshtein distance, capped by an early exit once the best possible result exceeds `max`. Used to
 * forgive small typos in a header ("Destinaton") without matching unrelated columns.
 */
const withinDistance = (a: string, b: string, max: number) => {
  if (Math.abs(a.length - b.length) > max) return false;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    if (Math.min(...current) > max) return false;
    previous = current;
  }

  return previous[b.length] <= max;
};

/**
 * Best-effort auto-mapping of the file's headers onto our booking fields.
 *
 * Exact (normalised) matches on the label or an alias win first, across every field, before any
 * fuzzy matching runs — otherwise a near-miss could claim a column that another field matches
 * exactly. Each column is used at most once.
 *
 * Ambiguity is left unmapped on purpose: when two headers are equally close, guessing is worse than
 * asking, since a wrong mapping silently books the wrong data.
 */
export const autoMapColumns = (headers: string[]): ColumnMapping => {
  const mapping = { ...EMPTY_MAPPING };
  const normalisedHeaders = headers.map(normaliseHeader);
  const taken = new Set<number>();

  const claim = (key: BookingFieldKey, index: number) => {
    mapping[key] = index;
    taken.add(index);
  };

  // Pass 1 — exact matches on the label or any alias.
  BOOKING_FIELDS.forEach((field) => {
    const candidates = [field.label, ...(field.aliases ?? [])].map(normaliseHeader);
    const index = normalisedHeaders.findIndex((header, i) => !taken.has(i) && header && candidates.includes(header));
    if (index >= 0) claim(field.key, index);
  });

  // Pass 2 — near misses, but only where exactly one column is close enough.
  BOOKING_FIELDS.filter((field) => mapping[field.key] === null).forEach((field) => {
    const candidates = [field.label, ...(field.aliases ?? [])].map(normaliseHeader);
    const matches = normalisedHeaders
      .map((header, i) => ({ header, i }))
      .filter(({ header, i }) => !taken.has(i) && header && candidates.some((candidate) => withinDistance(header, candidate, 2)));
    if (matches.length === 1) claim(field.key, matches[0].i);
  });

  return mapping;
};

/** True once every required field has a column. Drives the Next button on Match Columns. */
export const isMappingComplete = (mapping: ColumnMapping) => unmappedRequiredFields(mapping).length === 0;

/**
 * Required fields with no column behind them yet — what stands between the user and step 4.
 *
 * Shared so the step's banner and the Next button's tooltip name the same fields; two independent
 * lists of "what's missing" would eventually disagree.
 */
export const unmappedRequiredFields = (mapping: ColumnMapping) =>
  BOOKING_FIELDS.filter((field) => field.required && mapping[field.key] === null);

/**
 * Turn the mapped spreadsheet rows into the payload `/bulk/validate` takes.
 *
 * Keys match the booking field keys exactly, which is also what the backend's row service reads —
 * one vocabulary end to end, so a rename can't silently half-apply.
 *
 * `rowIndex` is the row's position in the DATA rows (not the file), which is what the review table
 * and every error message key on.
 */
export type ValidationRow = { row_index: number } & Partial<Record<BookingFieldKey, string>>;

/**
 * Cells go to the server exactly as the file had them, phone included.
 *
 * Normalising the phone to E.164 here was tried and taken out. It read as a safe tidy-up — "0412345678"
 * to "+61412345678" — but the rule cannot tell a national number's country from the number alone, so it
 * takes the operator's. An NZ mobile written locally as "0211234567" is also a valid Sydney landline,
 * and it was silently rewritten to +61211234567: a real number, for the wrong person, in a booking
 * nobody would think to question. The review table showed the original the whole time.
 *
 * That is the client's own contact data, and guessing at it is worse than passing through something the
 * server can refuse. Anything a spreadsheet damaged — a stripped "+", a lost leading zero — is now the
 * reviewer's to fix, where it is visible, rather than ours to infer.
 */
export const toValidationRows = (dataRows: string[][], mapping: ColumnMapping): ValidationRow[] =>
  dataRows.map((row, rowIndex) => {
    const value = (field: BookingFieldKey) => {
      const column = mapping[field];

      return column === null ? "" : (row[column] ?? "").trim();
    };

    return BOOKING_FIELDS.reduce<ValidationRow>((acc, field) => ({ ...acc, [field.key]: value(field.key) }), {
      row_index: rowIndex,
    });
  });

/** True when a row has nothing in any mapped column — a trailing blank line, not a booking. */
export const isBlankRow = (row: string[]) => row.every((cell) => !cell?.trim());
